/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchResultModalViewProps } from './components';
import { ICategoryHierarchyViewProps, IRefineMenuViewProps, ISearchResultContainerViewProps, ISortByViewProps, ITitleViewProps } from './c-search-result-container';
import { ICSearchResultContainerProps } from './c-search-result-container.props.autogenerated';

import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & ICSearchResultContainerProps<{}>> = props => {
    const { SearchResultContainer, products, pagination, ProductsContainer, ProductSectionContainer, choiceSummary, isMobile, modalToggle, searchResultModal, TitleViewProps,
        refineMenu, categoryHierarchy, sortByOptions, CategoryNavContainer, RefineAndProductSectionContainer, errorMessage, FeatureSearchContainer, similarLookProduct } = props;
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {redirectIfOne(props)}
                {renderCategoryHierarchy(categoryHierarchy)}
                {renderTitle(TitleViewProps)}
                {choiceSummary}
                {modalToggle}
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>
                    {similarLookProduct}
                </Node>
                <Node {...ProductsContainer}>
                    {errorMessage}
                    {products}
                </Node>
                {pagination}
            </Module>
        );
    }
    return (
        <Module {...SearchResultContainer}>
            <Node {...CategoryNavContainer}>
                {/* {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                {TitleViewProps && renderTitleCount(TitleViewProps)} */}
                <div className='row pc-category-nav-section'>
                    <div className='col-3 pc-category-hierarchy'>
                        {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    </div>
                    <div className='col-9'>
                        <div className='row pc-sort-count-page'>
                            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                            {TitleViewProps && renderTitleCount(TitleViewProps)}
                            {pagination}
                        </div>
                    </div>
                </div>
            </Node>
            <Node {...RefineAndProductSectionContainer}>
                {/* {refineMenu && renderRefiner(refineMenu)} */}
                <div className='pc-left-nav'>
                    <p className='pc-left-nav-title pc-featured'>{props.config.titleFeatured}</p>
                    {renderLNFeaturedLinks(props)}
                    <p className='pc-left-nav-title pc-category'>{props.config.titleCategory}</p>
                    {renderLNCatLinks(props)}
                    {refineMenu && renderRefiner(refineMenu)}
                </div>
                <Node {...ProductSectionContainer}>
                    {/* {TitleViewProps && renderTitle(TitleViewProps)} */}
                    {choiceSummary}
                    {/* {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)} */}
                    <Node {...FeatureSearchContainer}>
                        {similarLookProduct}
                    </Node>
                    <Node {...ProductsContainer}>
                        {errorMessage}
                        {products}
                    </Node>
                    {pagination}
                </Node>
            </Node>
        </Module>
    );

};

const redirectIfOne = (props: ISearchResultContainerViewProps & ICSearchResultContainerProps<{}>): void => {
    React.useEffect(() => {
        if (props.config.autoRedirect === true) { //props.config.autoRedirect
            const products = (props.data.listPageState.result && props.data.listPageState.result.activeProducts) || [];
            if (products.length === 1) {
                const productUrl = getProductPageUrlSync(products[0].Name || '', products[0].RecordId, props.context && props.context.actionContext, undefined);
                // useEffect(() => {
                global.location.href = productUrl
            // });
            }
        }
    });
}

const createSearchResultModal = (modalProps: ISearchResultModalViewProps, refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps, isRecoSearchPage?: string): JSX.Element => {
    return React.cloneElement(modalProps.modal, {}, modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage), modalProps.modalFooter);
};

const createModalBody = (props: ISearchResultModalViewProps, refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefiner(refineMenu));
    }
    return null;
};

const renderLNCatLinks = (props: ICSearchResultContainerProps<{}>): JSX.Element | null => {
    const lnLinks = props.config.linksCategory;
    if (lnLinks) {
        return (
            <ul className='pc-left-nav-links pc-category-links'>
                {lnLinks.map((linkCategory, index) => (
                    <li key={index} className='pc-left-nav-links-item'><a className='pc-left-nav-links-url' href={linkCategory.linkUrl.destinationUrl}>{linkCategory.linkText}</a></li>
                    )) 
                }   
            </ul> 
        );   
    }

    return null;
}

const renderLNFeaturedLinks = (props: ICSearchResultContainerProps<{}>): JSX.Element | null => {
    const lnLinks = props.config.linksFeatured;
    if (lnLinks) {
        return (
            <ul className='pc-left-nav-links pc-featured-links'>
                {lnLinks.map((linkFeatured, index) => (
                    <li key={index} className='pc-left-nav-links-item'><a className='pc-left-nav-links-url' href={linkFeatured.linkUrl.destinationUrl}>{linkFeatured.linkText}</a></li>
                    )) 
                }   
            </ul> 
        );   
    }

    return null;
}

const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>
                            {submenu}
                        </React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return (
            <Node {...SortingContainer}>
                {sortByDropDown}
            </Node>
        );
    }
    return null;
};

const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h2>
                    {title.titlePrefix}
                    {title.titleText}
                </h2>
            </Node>
        );
    }
    return null;
};

const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h5>
                    {title.titleCount}
                </h5>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
